// kernel/designer/jnn/browserCalls.ts
// The jnn brain's browser tools. The reads (read_scene, view_area, view_image) and crop_image run here, outside the script runner: nothing
// billed, no undo step, their only writes the images they store for the model. The actions are design commands run through the runner,
// one single-execution script each, so every guard of a command an agent issues applies (disabled
// commands, model lock, output scope, the generation's own billing, project kinds, stop), and no
// failure wakes the legacy assistant; the runner records each command's status and error on the
// script, which is what the call answers. The 8K and 16K Upscale run the Powers menu's own grid script instead.
import type { RefObject } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type {
  DesignerBrowserCall, DesignerCanvasView, DesignerImageView, DesignerItem, DesignerRestrictions, DesignerScene, DesignerStudioState, DesignerView, KernelHandle, Layer, Rect, ScriptInstance,
  UiCommand, UndoableAction, Workspace,
} from '#types/types';
import { uploadFileToCloud } from '#lib/azure-client';
import { clampNativeLayerCutBlendRadius, computeLayerRaster, getNativeLayerCutImageBounds } from '#lib/layer-cut-utils';
import { runLayerRasterInWorker } from '#lib/layer-raster-client';
import { runWithWorkerFallback } from '#lib/worker-rpc';
import { resolveStudioLayerTransform } from '#lib/studio-layer-transform';
import { sortLayersByRenderOrder } from '#lib/layer-render-order';
import { getDrawingLayerPayload } from '#lib/drawing-layer';
import { resolveWorkspaceAnnotationMask } from '#lib/workspace-annotations';
import { enforceStudioModelMinimumGenerations, getStudioModelReferenceLimits, shouldUnlockIncompatibleStudioModelPin } from '#lib/model-registry';
import { isInternalAssetUrl } from '#lib/azure-shared';
import { getFileExtension } from '#lib/utils';
import { DESIGNER_MODEL_VIEW_MAX_SIDE, isDesignerImageRef } from '#lib/designer/jnn/items';
import { designerLayerWorldRect, designerStepLabel, designerViewPointToWorld, designerViewRectToWorld, serializeDesignerScene } from '#lib/designer/jnn/scene';
import { getWorkspaceRasterDimensions } from '@/components/workspaceRasterUtils';
import {
  DESIGNER_BILLED_TOOLS, DESIGNER_JNN_ACTIONS, DESIGNER_JNN_REFUSED_MODELS, DESIGNER_SWEEP_UPSCALE_COMMANDS, DESIGNER_SWEEP_UPSCALE_TOOLS, DESIGNER_MAX_CUT_FEATHER, VIEW_AREA_MAX_SIDE, designerNewLayersReport, designerRefusedModel,
  designerRestrictions,
} from '#lib/designer/jnn/browserTools';
import { resolveDesignerJnnSettings } from '#lib/designer/jnn/settings';
import { agentCommandRefusal } from '#lib/designer/agentCommandRestriction';
import { commandAllowedInUnpromptedTurn, unpromptedTurnRefusal } from '#lib/leasing/unprompted-turns';

/** controller is the turn's own: a stop pressed while the runner holds the session's controller reaches the turn through isStopRequested. */
type CallContext = { projectId: string; workspaceId: string; sessionId: string; controller: AbortController };

const MAX_COMMAND_ERROR_CHARS = 500;
const MAX_REPORTED_LAYERS = 10;
/** A layer alone is drawn over this flat colour, which a photo never holds: what is left of a cut layer stands out. */
const LAYER_ALONE_BACKGROUND = '#FF00FF';

const isRect = (value: unknown): value is Rect => {
  const rect = value as Partial<Rect> | null;
  return Boolean(rect) && [rect!.x, rect!.y, rect!.w, rect!.h].every(n => typeof n === 'number' && Number.isFinite(n)) && rect!.w! > 0 && rect!.h! > 0;
};

/** The generation the browser would run now: what a budgeted turn's server quotes (lib/designer/jnn/budget.ts). */
export const readDesignerStudioState = (kernel: RefObject<KernelHandle | null>, projectId: string): DesignerStudioState | undefined => {
  if (!kernel.current) return undefined;
  const model = kernel.current.getDiffusionModel();
  const params = kernel.current.getModelParams()[model] as Record<string, unknown> | undefined;
  return { model, params: params ?? {}, outputs: enforceStudioModelMinimumGenerations(model, kernel.current.getLocalNumOutputs(projectId)) };
};

/** The user's locks on the action tools' commands, read as the legacy Director reads them, for the model's end-of-input note. */
export const readDesignerRestrictions = (kernel: RefObject<KernelHandle | null>, projectId: string): DesignerRestrictions => {
  const preferences = kernel.current?.getUserPreferences() ?? {};
  const lockedModelIncompatible = shouldUnlockIncompatibleStudioModelPin(
    preferences.lockSelectedModelForAgent === true, kernel.current?.getDiffusionModel() ?? '', kernel.current?.getInventory().Project.registry[projectId]?.projectType,
  );
  return designerRestrictions(preferences, lockedModelIncompatible);
};

/**
 * The reference tray as stored images, each stored once per turn: a file new to this turn is returned in fresh, for the model to look at
 * beside the scene, since an earlier turn's images are folded out of its context.
 */
const readReferenceTray = async (
  kernel: RefObject<KernelHandle | null>, { projectId, sessionId, controller: { signal } }: CallContext,
): Promise<{ tray: DesignerScene['referenceTray']; fresh: string[]; failed: string[] }> => {
  const stored = { ...kernel.current?.readEntityRuntimeState('ChatSession', sessionId).designerTray };
  const ownerId = kernel.current?.getInventory().Project.registry[projectId]?.userId;
  const fresh: string[] = [];
  const failed: string[] = [];
  const tray = await Promise.all((kernel.current?.getExtraFiles(projectId) ?? []).map(async ({ id, file }) => {
    if (stored[id] === undefined && file.type.startsWith('image/')) {
      const ref = await uploadFileToCloud(file, `designer_tray_${uuidv4()}.${getFileExtension(file.name) || 'bin'}`, file.type, signal, ownerId).catch((error: unknown) => {
        if (!signal.aborted) console.error('[designer] reference tray upload failed', error);
        return null;
      });
      // Tried once per turn: a failure is told once, never retried at every read.
      stored[id] = ref ?? '';
      if (ref) fresh.push(ref);
      else failed.push(file.name);
    }
    return { ref: stored[id] || null, name: file.name };
  }));
  signal.throwIfAborted();
  if (fresh.length || failed.length) kernel.current?.patchEntityRuntimeState('ChatSession', sessionId, { designerTray: stored });
  if (fresh.length) kernel.current?.registerCloudAssets(fresh, 'upload');
  if (failed.length) kernel.current?.handleError('A reference image could not be shared with the Designer. It still serves your generations.');
  return { tray, fresh, failed };
};

const readScene = async (kernel: RefObject<KernelHandle | null>, context: CallContext): Promise<DesignerItem['output']> => {
  const { projectId, workspaceId } = context;
  const project = kernel.current?.getInventory().Project.registry[projectId];
  const studio = readDesignerStudioState(kernel, projectId);
  if (!kernel.current || !project || !studio) return 'Error: the project is not open.';
  const { tray, fresh, failed } = await readReferenceTray(kernel, context);
  const scene: DesignerScene = {
    projectName: project.name ?? '',
    activeWorkspaceId: workspaceId || null,
    model: studio.model,
    modelParameters: studio.params,
    outputsPerGeneration: studio.outputs,
    referenceTray: tray,
    undoStep: kernel.current.getUndoStack().at(-1)?.description ?? null,
    redoStep: kernel.current.getRedoStack()[0]?.description ?? null,
    workspaces: (project.workspaceIds ?? []).flatMap(id => {
      const workspace = kernel.current?.getWorkspace(id);
      if (!workspace) return [];
      const versions = (workspace.versions ?? []).filter(version => !version.isLoopDraft);
      const activeVersion = versions.find(version => version.id === workspace.activeVersionId);
      // As the legacy turn reads them: the active version's markers, unless the user turned annotations off.
      const annotations = workspace.annotationsEnabled !== false;
      // A marker sits in the annotation raster's pixels, laid over the frame (components/AnnotationCanvas.tsx).
      const { scale } = getWorkspaceRasterDimensions(workspace.frameRect);
      return [{
        id: workspace.id, name: workspace.name, frame: workspace.frameRect, generationArea: workspace.generationRect,
        activeVersionId: workspace.activeVersionId, validatedVersionId: workspace.validatedVersionId,
        versions: versions.map(version => ({ id: version.id, name: version.name, status: version.status })),
        comments: annotations ? (activeVersion?.annotationComments ?? []).map(comment => ({
          x: workspace.frameRect.x + comment.x / scale, y: workspace.frameRect.y + comment.y / scale, text: comment.text,
        })) : [],
        strokes: annotations && Boolean(activeVersion?.lastAnnotationMaskUrl || workspace.annotationMask),
        layers: sortLayersByRenderOrder(kernel.current?.getLayersInWorkspaceContext(id) ?? []).map(layer => ({
          id: layer.id, name: layer.name, kind: layer.type ?? 'image', rect: designerLayerWorldRect(workspace.frameRect, layer.rect, layer.params), visible: layer.visible,
          pixelWidth: layer.rect.w / (layer.scale || 1), pixelHeight: layer.rect.h / (layer.scale || 1), locked: layer.locked === true, alwaysAbove: layer['top-layer'] === true,
        })),
      }];
    }),
  };
  const unshared = failed.length ? `\nThese reference tray images could not be shared with you, so you cannot see them, but generate still takes them: ${failed.join(', ')}.` : '';
  const text = `${serializeDesignerScene(scene)}${unshared}`;
  if (fresh.length === 0) return text;
  return [{ type: 'input_text', text }, ...fresh.flatMap(ref => [
    { type: 'input_text', text: `Reference tray image ${ref}:` },
    { type: 'input_image', image_url: ref, detail: 'high' },
  ])];
};

// renderWorkspaceArea draws a raster from its decoded pixels only and skips one without them.
const isUnloaded = (layer: Layer): boolean => !layer.type && !getDrawingLayerPayload(layer.params) && !layer.imageData;

/** The layers a version shows that the browser holds pixels for; a raster never decoded since the project opened is named, not loaded here. */
const versionLayers = (kernel: RefObject<KernelHandle | null>, workspace: Workspace, versionId: string): { layerIds: string[]; note: string } | string => {
  const version = (workspace.versions ?? []).find(candidate => candidate.id === versionId && !candidate.isLoopDraft);
  if (!version) return 'Error: no version with that id in this workspace.';
  const layers = new Map((kernel.current?.getLayersInWorkspaceContext(workspace.id) ?? []).map(layer => [layer.id, layer]));
  const visible = version.layerVisibility.flatMap(entry => {
    const layer = entry.visible ? layers.get(entry.id) : undefined;
    return layer ? [layer] : [];
  });
  const unloaded = visible.filter(isUnloaded).map(layer => layer.id);
  const layerIds = visible.filter(layer => !unloaded.includes(layer.id)).map(layer => layer.id);
  if (layerIds.length === 0) return unloaded.length ? `Error: the layers of this version are not loaded in the browser: ${unloaded.join(', ')}.` : 'Error: this version shows no layer.';
  return { layerIds, note: unloaded.length ? ` Not drawn, not loaded in the browser: ${unloaded.join(', ')}.` : '' };
};

/** One layer of the workspace by itself, whatever its visibility: explicit layer ids are drawn hidden or not. */
const loneLayer = (kernel: RefObject<KernelHandle | null>, workspace: Workspace, layerId: string): { layerIds: string[]; note: string } | string => {
  const layer = (kernel.current?.getLayersInWorkspaceContext(workspace.id) ?? []).find(candidate => candidate.id === layerId);
  if (!layer) return 'Error: no layer with that id in this workspace.';
  if (isUnloaded(layer)) return `Error: layer ${layerId} is not loaded in the browser.`;
  return { layerIds: [layer.id], note: '' };
};

/** The view a call's coordinates are pixels of; null when they are not a view's. Views live for the turn that took them: an earlier turn's images are folded out of the model's context. */
const readView = (kernel: RefObject<KernelHandle | null>, sessionId: string, viewId: unknown, tool: 'view_area' | 'view_image'): DesignerView | null | string => {
  if (viewId === null || viewId === undefined) return null;
  const view = typeof viewId === 'string' ? kernel.current?.readEntityRuntimeState('ChatSession', sessionId).designerViews?.[viewId] : undefined;
  return view ?? `Error: no view ${String(viewId)} in this turn; nothing was changed. Look again with ${tool} and point in the pixels of that new view.`;
};

/** A view of the canvas, whose pixels map to world units; a view of an image is refused. */
const readCanvasView = (kernel: RefObject<KernelHandle | null>, sessionId: string, viewId: unknown): DesignerCanvasView | null | string => {
  const view = readView(kernel, sessionId, viewId, 'view_area');
  if (view === null || typeof view === 'string' || 'worldRect' in view) return view;
  return `Error: view ${String(viewId)} is a view_image view of an image, not of the canvas; nothing was changed. Point in the pixels of a view_area view, or pass viewId null.`;
};

/** A view of an image, whose pixels map to that image's pixels; a view of the canvas is refused. */
const readImageView = (kernel: RefObject<KernelHandle | null>, sessionId: string, viewId: unknown): DesignerImageView | null | string => {
  const view = readView(kernel, sessionId, viewId, 'view_image');
  if (view === null || typeof view === 'string' || !('worldRect' in view)) return view;
  return `Error: view ${String(viewId)} is a view_area view of the canvas; nothing was changed. view_image and crop_image take the viewId of a view_image view, or null.`;
};

// Refused rather than clamped: a point outside the image designates something the agent never saw.
const outsideView = (viewId: unknown, view: DesignerView): string =>
  `Error: those coordinates fall outside view ${String(viewId)}, whose image is ${view.pxWidth}x${view.pxHeight} px; nothing was changed.`;

/** A look's longest side: the one asked, else the user's viewSide setting. */
const viewMaxSide = (kernel: RefObject<KernelHandle | null>, asked: unknown): number =>
  Math.min(VIEW_AREA_MAX_SIDE, Math.max(64, typeof asked === 'number' ? Math.round(asked) : resolveDesignerJnnSettings(kernel.current?.getUserPreferences()?.designerJnn).viewSide));

const fitSide = (width: number, height: number, maxSide: number): { width: number; height: number } => {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

/** An area of an image drawn at a size and encoded; transparent parts over `background` when one is given. */
const encodeImage = async (
  source: CanvasImageSource, area: Rect, size: { width: number; height: number }, type: string, quality?: number, background?: string,
): Promise<Blob> => {
  const canvas = new OffscreenCanvas(size.width, size.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2D context to encode an image.');
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, size.width, size.height);
  }
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, area.x, area.y, area.w, area.h, 0, 0, size.width, size.height);
  return canvas.convertToBlob({ type, quality });
};

// The legacy Director's model view (lib/llmClient.ts downscaleImageForModel), drawn here: this brain's server touches no pixel.
const MODEL_VIEW_QUALITY = 0.82;
const MODEL_VIEW_UNTOUCHED_BYTES = 512 * 1024;
const VIEW_IMAGE_QUALITY = 0.9;

/**
 * The image the model is shown of a chat attachment, in its EXIF orientation: view null when the original serves,
 * or a smaller WebP fitted to 1568 px, alpha kept, stored by `store`; size null when the browser cannot decode it.
 */
export const designerModelView = async (
  source: Blob, store: (view: Blob) => Promise<string | null>,
): Promise<{ view: string | null; size: { width: number; height: number } | null }> => {
  const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' }).catch(() => null);
  if (!bitmap) return { view: null, size: null };
  try {
    const size = { width: bitmap.width, height: bitmap.height };
    if (Math.max(size.width, size.height) <= DESIGNER_MODEL_VIEW_MAX_SIDE && source.size <= MODEL_VIEW_UNTOUCHED_BYTES) return { view: null, size };
    const view = await encodeImage(bitmap, { x: 0, y: 0, w: size.width, h: size.height }, fitSide(size.width, size.height, DESIGNER_MODEL_VIEW_MAX_SIDE), 'image/webp', MODEL_VIEW_QUALITY)
      .catch(() => null);
    // A browser without a WebP encoder answers PNG, rarely the smaller.
    if (!view || view.type !== 'image/webp' || view.size >= source.size) return { view: null, size };
    return { view: await store(view).catch(() => null), size };
  } finally {
    bitmap.close();
  }
};

/** An image of this conversation, read through the access-checked asset route and decoded in its EXIF orientation; null when it cannot be. */
const loadDesignerImage = async (path: string, signal: AbortSignal): Promise<ImageBitmap | null> => {
  const response = await fetch(path, { signal });
  if (!response.ok) return null;
  return createImageBitmap(await response.blob(), { imageOrientation: 'from-image' }).catch(() => null);
};

/** A new image stored for the model, owned by the project's owner as view_area's are. */
const storeDesignerImage = async (kernel: RefObject<KernelHandle | null>, projectId: string, file: Blob, name: string, signal: AbortSignal): Promise<string | null> => {
  const ref = await uploadFileToCloud(file, name, file.type, signal, kernel.current?.getInventory().Project.registry[projectId]?.userId);
  if (ref) kernel.current?.registerCloudAssets([ref], 'upload');
  return ref;
};

/** The image an image tool works on and the view its coordinates are pixels of, or the refusal. */
const readImageTarget = (kernel: RefObject<KernelHandle | null>, sessionId: string, args: { image?: unknown; viewId?: unknown }): { image: string; view: DesignerImageView | null } | string => {
  if (!isDesignerImageRef(args.image)) return 'Error: image must be the /api/assets/ path of an image of this conversation.';
  const view = readImageView(kernel, sessionId, args.viewId);
  if (typeof view === 'string') return view;
  if (view && view.image !== args.image) return `Error: view ${String(args.viewId)} shows ${view.image}, not that image; nothing was changed.`;
  return { image: args.image, view };
};

/**
 * A point in pixels of an image view, in the image's own pixels; null outside the view, edges included in it. Each axis at
 * its own scale, unlike a canvas view: the view's short side is rounded, and one scale would carry its far edge off the image.
 */
const imageViewPoint = (view: DesignerImageView, px: { x: number; y: number }): { x: number; y: number } | null =>
  (px.x >= 0 && px.y >= 0 && px.x <= view.pxWidth && px.y <= view.pxHeight
    ? { x: view.srcRect.x + px.x * view.srcRect.w / view.pxWidth, y: view.srcRect.y + px.y * view.srcRect.h / view.pxHeight }
    : null);

const imageViewRect = (view: DesignerImageView, pxRect: Rect): Rect | null => {
  const topLeft = imageViewPoint(view, pxRect);
  const bottomRight = imageViewPoint(view, { x: pxRect.x + pxRect.w, y: pxRect.y + pxRect.h });
  return topLeft && bottomRight ? { x: topLeft.x, y: topLeft.y, w: bottomRight.x - topLeft.x, h: bottomRight.y - topLeft.y } : null;
};

const pixelBox = (rect: Rect): string => `x=${Math.round(rect.x)} y=${Math.round(rect.y)} w=${Math.round(rect.w)} h=${Math.round(rect.h)}`;

const viewImage = async (kernel: RefObject<KernelHandle | null>, rawArgs: string, { projectId, sessionId, controller: { signal } }: CallContext): Promise<DesignerItem['output']> => {
  const args = JSON.parse(rawArgs) as { image?: unknown; rect?: unknown; maxSide?: unknown; viewId?: unknown };
  const target = readImageTarget(kernel, sessionId, args);
  if (typeof target === 'string') return target;
  const { image, view } = target;
  const asked = args.rect ?? (view ? { x: 0, y: 0, w: view.pxWidth, h: view.pxHeight } : null);
  if (asked !== null && !isRect(asked)) return 'Error: rect needs a finite x, y and a positive w, h.';
  const viewed = asked && view ? imageViewRect(view, asked) : asked;
  if (asked && !viewed) return outsideView(args.viewId, view!);
  const bitmap = await loadDesignerImage(image, signal);
  if (!bitmap) return 'Error: that image could not be read.';
  const source = { width: bitmap.width, height: bitmap.height };
  const area = viewed ?? { x: 0, y: 0, w: source.width, h: source.height };
  // A view's far edge, converted back, can land a rounding error past the image.
  if (area.x < 0 || area.y < 0 || area.x + area.w > source.width + 1e-6 || area.y + area.h > source.height + 1e-6) {
    bitmap.close();
    return `Error: rect falls outside the image, which is ${source.width}x${source.height} px; nothing was changed.`;
  }
  const size = fitSide(area.w, area.h, viewMaxSide(kernel, args.maxSide));
  const file = await encodeImage(bitmap, area, size, 'image/jpeg', VIEW_IMAGE_QUALITY, LAYER_ALONE_BACKGROUND).finally(() => bitmap.close());
  signal.throwIfAborted();
  const ref = await storeDesignerImage(kernel, projectId, file, `designer_view_${uuidv4()}.jpg`, signal);
  if (!ref) return 'Error: the image could not be saved.';
  const newViewId = `view-${uuidv4().slice(0, 6)}`;
  const views = kernel.current?.readEntityRuntimeState('ChatSession', sessionId).designerViews;
  kernel.current?.patchEntityRuntimeState('ChatSession', sessionId, { designerViews: { ...views, [newViewId]: { image, srcRect: area, pxWidth: size.width, pxHeight: size.height } } });
  return [
    { type: 'input_text', text: `Image ${image}, area ${pixelBox(area)} of its ${source.width}x${source.height} px, shown at ${size.width}x${size.height} px, any transparent part in flat magenta, viewId ${newViewId}. Image URL: ${ref}.` },
    { type: 'input_image', image_url: ref, detail: 'high' },
  ];
};

const isPolygonList = (value: unknown): value is Array<Array<{ x: number; y: number }>> => Array.isArray(value) && value.every(polygon => Array.isArray(polygon)
  && polygon.every(point => typeof point?.x === 'number' && Number.isFinite(point.x) && typeof point?.y === 'number' && Number.isFinite(point.y)));

const keepsAPixel = (pixels: ImageData): boolean => {
  for (let at = 3; at < pixels.data.length; at += 4) if (pixels.data[at]! > 0) return true;
  return false;
};

const cropImage = async (kernel: RefObject<KernelHandle | null>, rawArgs: string, { projectId, sessionId, controller: { signal } }: CallContext): Promise<DesignerItem['output']> => {
  const args = JSON.parse(rawArgs) as { image?: unknown; polygons?: unknown; featherRadius?: unknown; keepOutside?: unknown; viewId?: unknown };
  const target = readImageTarget(kernel, sessionId, args);
  if (typeof target === 'string') return target;
  const { image, view } = target;
  if (!isPolygonList(args.polygons)) return 'Error: polygons must be lists of {x, y} points.';
  if (args.polygons.length === 0 || args.polygons.some(polygon => polygon.length < 3)) return 'Error: each polygon needs at least 3 points; nothing was made.';
  const polygons = view ? args.polygons.map(polygon => polygon.map(point => imageViewPoint(view, point))) : args.polygons;
  if (!polygons.every(polygon => polygon.every(Boolean))) return outsideView(args.viewId, view!);
  const bitmap = await loadDesignerImage(image, signal);
  if (!bitmap) return 'Error: that image could not be read.';
  const source = new OffscreenCanvas(bitmap.width, bitmap.height);
  const sourceCtx = source.getContext('2d', { willReadFrequently: true });
  sourceCtx?.drawImage(bitmap, 0, 0);
  bitmap.close();
  if (!sourceCtx) return 'Error: the image could not be cut.';
  const pixels = sourceCtx.getImageData(0, 0, source.width, source.height);
  const keepOutside = args.keepOutside === true;
  const cutPolygons = polygons as Array<Array<{ x: number; y: number }>>;
  // Clipped to the image, as the cut clips them; one wholly outside it designates nothing the agent saw.
  const inside = getNativeLayerCutImageBounds(pixels.width, pixels.height, cutPolygons);
  if (!inside) return `Error: those polygons fall entirely outside the image, which is ${pixels.width}x${pixels.height} px; nothing was made.`;
  const whole = { x: 0, y: 0, w: pixels.width, h: pixels.height };
  const job = {
    blendRadius: clampNativeLayerCutBlendRadius(args.featherRadius),
    // The image's own borders are no cut edge: placed in a frame that is the image, no fade starts from them.
    placement: {
      rect: keepOutside ? whole : { x: inside.minX, y: inside.minY, w: inside.width, h: inside.height },
      scale: 1, transform: resolveStudioLayerTransform(null), frameRect: whole,
    },
    cut: { polygons: cutPolygons, invert: keepOutside },
  };
  const workerRef = kernel.current?.blendWorkerRef ?? { current: null };
  const { result } = await runWithWorkerFallback(workerRef, worker => runLayerRasterInWorker(worker, pixels, job), () => computeLayerRaster(pixels, job),
    '[designer] crop_image raster worker failed, cutting on the main thread.');
  if (!result || !keepsAPixel(result)) return 'Error: those polygons keep nothing of the image; nothing was made.';
  signal.throwIfAborted();
  const canvas = new OffscreenCanvas(result.width, result.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return 'Error: the image could not be cut.';
  ctx.putImageData(result, 0, 0);
  const cut = { x: 0, y: 0, w: result.width, h: result.height };
  const [png, preview] = await Promise.all([
    canvas.convertToBlob({ type: 'image/png' }),
    encodeImage(canvas, cut, fitSide(result.width, result.height, viewMaxSide(kernel, null)), 'image/jpeg', VIEW_IMAGE_QUALITY, LAYER_ALONE_BACKGROUND),
  ]);
  const ref = await storeDesignerImage(kernel, projectId, png, `designer_crop_${uuidv4()}.png`, signal);
  if (!ref) return 'Error: the image could not be saved.';
  const shown = await storeDesignerImage(kernel, projectId, preview, `designer_view_${uuidv4()}.jpg`, signal);
  const made = `Done. New image ${ref}, ${result.width}x${result.height} px PNG, cut from ${image}${keepOutside ? ' keeping the outside of the polygons' : ''}; transparent where the cut removed`;
  // The crop exists: a lost preview costs only the look, which view_image gives again.
  if (!shown) return `${made}. Its preview could not be saved: look at it with view_image.`;
  return [
    { type: 'input_text', text: `${made}, shown here on flat magenta.` },
    { type: 'input_image', image_url: shown, detail: 'high' },
  ];
};

const viewArea = async (kernel: RefObject<KernelHandle | null>, rawArgs: string, { projectId, sessionId, controller: { signal } }: CallContext): Promise<DesignerItem['output']> => {
  const args = JSON.parse(rawArgs) as { workspaceId?: unknown; rect?: unknown; maxSide?: unknown; versionId?: unknown; layerId?: unknown; viewId?: unknown; annotations?: unknown };
  const workspace = typeof args.workspaceId === 'string' ? kernel.current?.getWorkspace(args.workspaceId) : null;
  if (!workspace || workspace.projectId !== projectId) return 'Error: no workspace with that id in this project.';
  const view = readCanvasView(kernel, sessionId, args.viewId);
  if (typeof view === 'string') return view;
  const asked = args.rect ?? (view ? { x: 0, y: 0, w: view.pxWidth, h: view.pxHeight } : workspace.frameRect);
  if (!isRect(asked)) return 'Error: rect needs a finite x, y and a positive w, h.';
  const rect = view ? designerViewRectToWorld(view, asked) : asked;
  if (!rect) return outsideView(args.viewId, view!);
  const versionId = typeof args.versionId === 'string' ? args.versionId : null;
  const layerId = typeof args.layerId === 'string' ? args.layerId : null;
  if (versionId && layerId) return 'Error: pass a versionId or a layerId, not both.';
  // The strokes belong to the canvas as it is now, as the legacy turn overlays them; a version or a lone layer would carry another's.
  if (args.annotations === true && (versionId || layerId)) return 'Error: annotation strokes are drawn over the canvas as it is now only: pass versionId and layerId null.';
  const annotations = args.annotations === true && workspace.annotationsEnabled !== false && Boolean(resolveWorkspaceAnnotationMask(workspace));
  const shown = versionId ? versionLayers(kernel, workspace, versionId) : layerId ? loneLayer(kernel, workspace, layerId) : null;
  if (typeof shown === 'string') return shown;
  // The user's view follows what the agent looks at, so they see where it is working.
  kernel.current?.zoomToFitRectInViewport(projectId, rect);
  const maxSide = viewMaxSide(kernel, args.maxSide);
  const targetPixels = Math.round(maxSide * maxSide * Math.min(rect.w, rect.h) / Math.max(rect.w, rect.h));
  // A version is drawn from its own visible layers, the canvas left as it is: no version is applied.
  const rendered = await kernel.current?.renderWorkspaceArea(
    workspace.id, shown?.layerIds, targetPixels, false, false, 1, rect, 'jpeg', undefined, annotations ? 'drawAnnotationOverlay' : undefined, undefined, undefined, layerId ? LAYER_ALONE_BACKGROUND : undefined,
  );
  if (!rendered?.file) return 'Error: the area could not be rendered.';
  signal.throwIfAborted();
  const ownerId = kernel.current?.getInventory().Project.registry[projectId]?.userId;
  const ref = await uploadFileToCloud(rendered.file, `designer_view_${uuidv4()}.jpg`, 'image/jpeg', signal, ownerId);
  if (!ref) return 'Error: the image could not be saved.';
  kernel.current?.registerCloudAssets([ref], 'upload');
  // Random, not counted: a folded earlier turn still names its views in its action log.
  const newViewId = `view-${uuidv4().slice(0, 6)}`;
  const views = kernel.current?.readEntityRuntimeState('ChatSession', sessionId).designerViews;
  kernel.current?.patchEntityRuntimeState('ChatSession', sessionId, { designerViews: { ...views, [newViewId]: { worldRect: rect, pxWidth: rendered.targetWidth, pxHeight: rendered.targetHeight } } });
  return [
    { type: 'input_text', text: `Workspace "${workspace.name}"${versionId ? `, version ${versionId}` : layerId ? `, layer ${layerId} alone on magenta` : ''}, area x=${Math.round(rect.x)} y=${Math.round(rect.y)} w=${Math.round(rect.w)} h=${Math.round(rect.h)}, ${rendered.targetWidth}x${rendered.targetHeight} px, viewId ${newViewId}.${shown?.note ?? ''}${annotations ? " The user's annotation strokes are drawn over it." : args.annotations === true ? ' No annotation strokes to draw: none are shown and loaded on this workspace.' : ''} Image URL: ${ref}.` },
    { type: 'input_image', image_url: ref, detail: 'high' },
  ];
};

const projectWorkspaceIds = (kernel: RefObject<KernelHandle | null>, projectId: string): string[] => [...(kernel.current?.getInventory().Project.registry[projectId]?.workspaceIds ?? [])];

const projectLayers = (kernel: RefObject<KernelHandle | null>, projectId: string): Map<string, string> => new Map(
  projectWorkspaceIds(kernel, projectId)
    .flatMap(workspaceId => (kernel.current?.getLayersInWorkspaceContext(workspaceId) ?? []).map(layer => [layer.id, workspaceId] as const)));

/** The layers a generation produced, for the model to read or look at next, with what each colour-matched one changes; null when none is on the canvas. */
const listNewLayers = (kernel: RefObject<KernelHandle | null>, projectId: string, before: Map<string, string>): string | null => {
  const created = [...projectLayers(kernel, projectId)].filter(([layerId]) => !before.has(layerId));
  if (created.length === 0) return null;
  return designerNewLayersReport(created.slice(0, MAX_REPORTED_LAYERS).map(([layerId, workspaceId]) => ({
    id: layerId,
    name: kernel.current?.getLayerRegistry()[layerId]?.name ?? '',
    workspaceId,
    activeVersionId: kernel.current?.getWorkspace(workspaceId)?.activeVersionId ?? null,
    change: kernel.current?.readEntityRuntimeState('StudioLayer', layerId).generationChange ?? null,
  })), created.length);
};

const describeNewLayers = (kernel: RefObject<KernelHandle | null>, projectId: string, before: Map<string, string>): string => {
  const created = listNewLayers(kernel, projectId, before);
  return created ? `Done. ${created}` : 'Done, but no new layer is on the canvas yet.';
};

/** A generation's references as the command loads them: our own image paths as given, a layer id as its stored image. */
const resolveReferences = (kernel: RefObject<KernelHandle | null>, references: unknown, layers: Map<string, string>): string[] | string => {
  if (!Array.isArray(references)) return 'Error: referenceImageUrls must be a list or null.';
  const urls: string[] = [];
  for (const reference of references) {
    if (isDesignerImageRef(reference)) { urls.push(reference as string); continue; }
    const source = typeof reference === 'string' && layers.has(reference) ? kernel.current?.getLayerRegistry()[reference]?.imageSrc : undefined;
    if (!source || !isInternalAssetUrl(source)) return `Error: "${String(reference)}" is neither an image of this conversation nor a layer with a stored image in this project.`;
    urls.push(source);
  }
  // The command loads each image once (loadListReferenceImages): an image named twice counts once.
  const unique = [...new Set(urls)];
  const model = kernel.current?.getDiffusionModel() ?? '';
  const { max } = getStudioModelReferenceLimits(model);
  if (unique.length > max) return `Error: ${model} takes at most ${max} reference image${max === 1 ? '' : 's'}; nothing was generated.`;
  return unique;
};

/** An action's geometry given in pixels of a view, in the world units its command takes, or the refusal. */
const viewPixelsToWorld = (viewId: unknown, view: DesignerCanvasView, args: Record<string, unknown>): Record<string, unknown> | string => {
  if (Array.isArray(args.polygons)) {
    const polygons = (args.polygons as Array<Array<{ x: number; y: number }>>).map(polygon => polygon.map(point => designerViewPointToWorld(view, point)));
    return polygons.every(polygon => polygon.every(Boolean)) ? { polygons } : outsideView(viewId, view);
  }
  const pxRect = { x: Number(args.x), y: Number(args.y), w: Number(args.width), h: Number(args.height) };
  if (!isRect(pxRect)) return `Error: width and height must be positive pixels of view ${String(viewId)}; nothing was changed.`;
  const area = designerViewRectToWorld(view, pxRect);
  return area ? { x: area.x, y: area.y, width: area.w, height: area.h } : outsideView(viewId, view);
};

const worldBox = (rect: Rect): string => `x=${Math.round(rect.x)} y=${Math.round(rect.y)} w=${Math.round(rect.w)} h=${Math.round(rect.h)} (world units)`;

/**
 * What a call through a view applied, in world units: the view expires with the turn and its pixels are folded out
 * of later turns' context. The area is read back, since the command keeps it inside the frame.
 */
const appliedThroughView = (kernel: RefObject<KernelHandle | null>, workspaceId: string, args: Record<string, unknown>): string | null => {
  if (Array.isArray(args.polygons)) {
    const points = (args.polygons as Array<Array<{ x: number; y: number }>>).flat();
    if (points.length === 0) return null;
    const xs = points.map(point => point.x);
    const ys = points.map(point => point.y);
    return `Done. Cut within ${worldBox({ x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) })}.`;
  }
  const area = kernel.current?.getWorkspace(workspaceId)?.generationRect;
  return area ? `Done. Generation area ${worldBox(area)}.` : null;
};

/** The images the user attached in this conversation, the only ones start_from_attachment may load: a model-named address is never fetched. */
const conversationAttachments = (kernel: RefObject<KernelHandle | null>, sessionId: string): string[] =>
  (kernel.current?.getChatSessionMessages(sessionId) ?? [])
    .flatMap(message => (message.role === 'user' ? [...(message.imageUrls ?? []), ...(message.images ?? []).map(image => image.url)] : []))
    .filter(isDesignerImageRef);

/**
 * The refusal of an action whose command would act on the wrong thing without failing: an unknown version is skipped silently, and the undo
 * history is shared with the user's own actions in every project, so undo and redo run only on the step the agent names.
 */
const actionRefusal = (kernel: RefObject<KernelHandle | null>, command: string, args: Record<string, unknown>, workspaceId: string, sessionId: string): string | null => {
  if (command === 'CHECKOUT') {
    const versions = (kernel.current?.getWorkspace(workspaceId)?.versions ?? []).filter(version => !version.isLoopDraft);
    return versions.some(version => version.id === args.versionId) ? null : 'Error: no version with that id in this workspace.';
  }
  if (command === 'SELECT_MODEL' && DESIGNER_JNN_REFUSED_MODELS.has(args.modelId as string)) return designerRefusedModel(args.modelId as string);
  if (command === 'GENERATE_MEDIA' && DESIGNER_JNN_REFUSED_MODELS.has(kernel.current?.getDiffusionModel() ?? '')) {
    return `Error: the selected model, ${kernel.current?.getDiffusionModel()}, is a model the Designer does not use; nothing was generated. Select another model with select_model first.`;
  }
  if (command === 'CREATE_LAYER_FROM_URL') {
    return conversationAttachments(kernel, sessionId).includes(args.url as string) ? null : 'Error: url is not an image the user attached in this conversation; nothing was added.';
  }
  if (command === 'UNDO' || command === 'REDO') {
    const step = undoTarget(kernel, command);
    const verb = command === 'UNDO' ? 'undo' : 'redo';
    if (!step) return `Error: there is nothing to ${verb}.`;
    const label = designerStepLabel(step.description);
    const steps = kernel.current?.readEntityRuntimeState('ChatSession', sessionId).designerSteps;
    // Descriptions repeat ("Hide layer") and the history spans projects: only the step object itself proves whose it is.
    if (!(command === 'UNDO' ? steps?.made : steps?.undone)?.includes(step)) {
      const keys = command === 'UNDO' ? 'Ctrl+Z (Cmd+Z on a Mac)' : 'Ctrl+Shift+Z (Cmd+Shift+Z on a Mac)';
      return `Error: nothing was ${verb}ne: the step ${verb} would take, "${label}", is not one you ${command === 'UNDO' ? 'made' : 'undid'} in this answer. If the user wants it ${verb}ne, ask them to press ${keys}.`;
    }
    return label === args.expectedStep ? null : `Error: nothing was ${verb}ne: the step ${verb} would take is "${label}", not "${String(args.expectedStep)}".`;
  }
  return null;
};

/** The step undo would revert, or the one redo would reapply. */
const undoTarget = (kernel: RefObject<KernelHandle | null>, command: 'UNDO' | 'REDO'): UndoableAction | undefined =>
  (command === 'UNDO' ? kernel.current?.getUndoStack().at(-1) : kernel.current?.getRedoStack()[0]);

/** Records as this turn's own the steps an action left on the undo history, or the step an undo or redo moved. */
const recordSteps = (kernel: RefObject<KernelHandle | null>, sessionId: string, command: string, before: readonly UndoableAction[], moved: UndoableAction | undefined): void => {
  const steps = kernel.current?.readEntityRuntimeState('ChatSession', sessionId).designerSteps ?? { made: [], undone: [] };
  const next = command === 'UNDO' && moved ? { made: steps.made, undone: [...steps.undone, moved] }
    : command === 'REDO' && moved ? { made: steps.made, undone: steps.undone.filter(step => step !== moved) }
      : { made: [...steps.made, ...(kernel.current?.getUndoStack() ?? []).filter(step => !before.includes(step))], undone: steps.undone };
  kernel.current?.patchEntityRuntimeState('ChatSession', sessionId, { designerSteps: next });
};

/** The runner takes the session's controller for its script and clears it after: the stop button must reach the turn again. */
const restoreTurnController = (kernel: RefObject<KernelHandle | null>, context: CallContext): void => {
  if (kernel.current && !kernel.current.readEntityRuntimeState('ChatSession', context.sessionId).abortController) {
    kernel.current.patchEntityRuntimeState('ChatSession', context.sessionId, { abortController: context.controller });
  }
};

const runAction = async (kernel: RefObject<KernelHandle | null>, call: DesignerBrowserCall, context: CallContext): Promise<string> => {
  const action = DESIGNER_JNN_ACTIONS[call.name]!;
  const args = JSON.parse(call.args) as Record<string, unknown>;
  const before = projectLayers(kernel, context.projectId);
  const workspacesBefore = projectWorkspaceIds(kernel, context.projectId);
  let workspaceId = context.workspaceId;
  if ('workspaceId' in args) {
    const workspace = typeof args.workspaceId === 'string' ? kernel.current?.getWorkspace(args.workspaceId) : null;
    if (!workspace || workspace.projectId !== context.projectId) return 'Error: no workspace with that id in this project.';
    workspaceId = workspace.id;
  }
  // A layer tool runs in its layer's workspace.
  if ('layerId' in args) {
    const layerWorkspaceId = typeof args.layerId === 'string' ? before.get(args.layerId) : undefined;
    if (!layerWorkspaceId) return 'Error: no layer with that id in this project.';
    workspaceId = layerWorkspaceId;
  }
  const view = readCanvasView(kernel, context.sessionId, args.viewId);
  if (typeof view === 'string') return view;
  if (view) {
    const world = viewPixelsToWorld(args.viewId, view, args);
    if (typeof world === 'string') return world;
    Object.assign(args, world);
  }
  if (args.referenceImageUrls !== null && args.referenceImageUrls !== undefined) {
    const references = resolveReferences(kernel, args.referenceImageUrls, before);
    if (typeof references === 'string') return references;
    args.referenceImageUrls = references.length > 0 ? references : null;
  }
  const refusal = actionRefusal(kernel, action.command, args, workspaceId, context.sessionId);
  if (refusal) return refusal;
  // A null argument is the tool's "leave it to the command": the command never sees it.
  const parameters = Object.fromEntries(action.parameters.flatMap(name => (args[name] === null || args[name] === undefined ? [] : [[name, args[name]]])));
  if (action.workspaceParameter) parameters[action.workspaceParameter] = workspaceId;
  // The user's colour-match setting governs their own generations only: the agent's are colour-matched unless it passes false.
  if (action.command === 'GENERATE_MEDIA') parameters.autoColorMatch = args.autoColorMatch !== false;
  if (action.command === 'CUT_LAYER' && typeof parameters.featherRadius === 'number') parameters.featherRadius = Math.min(parameters.featherRadius, DESIGNER_MAX_CUT_FEATHER);
  const historyBefore = [...(kernel.current?.getUndoStack() ?? [])];
  const moved = action.command === 'UNDO' || action.command === 'REDO' ? undoTarget(kernel, action.command) : undefined;
  const command: UiCommand = { id: `designer-${uuidv4()}`, action_type: action.command, parameters };
  const script: ScriptInstance = { id: `designer-script-${uuidv4()}`, label: `Designer: ${call.name}`, commands: [command], autorun: true, isAgentGenerated: true };
  try {
    await kernel.current?.runGeneratedScriptLogic(workspaceId, script, undefined, { sessionId: context.sessionId, singleExecution: true });
  } finally {
    restoreTurnController(kernel, context);
  }
  if (kernel.current?.readEntityRuntimeState('ChatSession', context.sessionId).isStopRequested) context.controller.abort();
  context.controller.signal.throwIfAborted();
  // The runner's errors are written for the agent that issued the command: they name the remedy.
  if (command.status !== 'success') return `Error: ${(command.error ?? 'the command did not run.').slice(0, MAX_COMMAND_ERROR_CHARS)}`;
  recordSteps(kernel, context.sessionId, action.command, historyBefore, moved);
  if (DESIGNER_BILLED_TOOLS.has(call.name) || action.command === 'CREATE_LAYER_FROM_URL') return describeNewLayers(kernel, context.projectId, before);
  if (action.command === 'CHECKOUT') return `Done. Version ${String(args.versionId)} is active.`;
  if (action.command === 'DUPLICATE_WORKSPACE') {
    const created = projectWorkspaceIds(kernel, context.projectId).filter(id => !workspacesBefore.includes(id));
    if (created.length === 0) return 'Error: no workspace was created.';
    const switched = args.switchToNew === true ? ` The user's active workspace is now ${created[0]}: read_scene before you act on the active workspace.` : '';
    return `Done. New workspace${created.length === 1 ? '' : 's'}: ${created.map(id => `${id} "${kernel.current?.getWorkspace(id)?.name ?? ''}"`).join(', ')}.${switched}`;
  }
  // The kernel starts the step's own undo or redo without awaiting it: the canvas may not show it yet.
  if (action.command === 'UNDO' || action.command === 'REDO') return `${action.command === 'UNDO' ? 'Undo' : 'Redo'} of "${String(args.expectedStep)}" started; read_scene or view_area before you rely on its result.`;
  if (action.command === 'SELECT_MODEL') return `Done. Selected model: ${kernel.current?.getDiffusionModel() ?? 'unknown'}.`;
  if (action.command === 'SET_LAYER_VISIBILITY') return `Done. Layer ${String(args.layerId)} is ${args.visible ? 'shown' : 'hidden'}.`;
  const applied = view ? appliedThroughView(kernel, workspaceId, args) : null;
  if (applied) return applied;
  return command.resultSummary ? `Done. ${command.resultSummary}` : 'Done.';
};

/**
 * The runner's own refusal, in its order and words, of a sweep the user's settings keep from the agent: the grid script is the user's
 * power, not an agent script, so the runner checks none of it.
 */
const sweepUpscaleRefusal = (kernel: RefObject<KernelHandle | null>, { projectId, sessionId }: CallContext): string | null => {
  if ((kernel.current?.readEntityRuntimeState('ChatSession', sessionId).unpromptedTurnDepth ?? 0) > 0 && !commandAllowedInUnpromptedTurn('GENERATE_MEDIA')) {
    return unpromptedTurnRefusal('GENERATE_MEDIA');
  }
  const { disabled, modelLocked } = readDesignerRestrictions(kernel, projectId);
  const off = DESIGNER_SWEEP_UPSCALE_COMMANDS.find(command => disabled.includes(command));
  if (off) return agentCommandRefusal(off, 'disabled');
  const locked = DESIGNER_SWEEP_UPSCALE_COMMANDS.find(command => modelLocked.includes(command));
  return locked ? agentCommandRefusal(locked, 'model-locked') : null;
};

/**
 * The 8K or 16K Upscale as the Powers menu runs it. Its grid batch is a runner script on the turn's conversation, so a stop reaches it as any
 * agent generation: the session's controller aborts the tile in flight and isStopRequested halts the next. The runner swallows a failed tile,
 * which only the script's commands tell.
 */
const runSweepUpscale = async (kernel: RefObject<KernelHandle | null>, call: DesignerBrowserCall, context: CallContext): Promise<string> => {
  const args = JSON.parse(call.args) as { workspaceId?: unknown };
  const workspace = typeof args.workspaceId === 'string' ? kernel.current?.getWorkspace(args.workspaceId) : null;
  if (!workspace || workspace.projectId !== context.projectId) return 'Error: no workspace with that id in this project.';
  const refusal = sweepUpscaleRefusal(kernel, context);
  if (refusal) return `Error: ${refusal}`;
  const before = projectLayers(kernel, context.projectId);
  let script: ScriptInstance | undefined;
  try {
    script = await kernel.current?.sweepUpscale(workspace.id, DESIGNER_SWEEP_UPSCALE_TOOLS[call.name]!, { sessionId: context.sessionId });
  } finally {
    restoreTurnController(kernel, context);
  }
  if (kernel.current?.readEntityRuntimeState('ChatSession', context.sessionId).isStopRequested) context.controller.abort();
  context.controller.signal.throwIfAborted();
  const failed = script?.commands.find(command => command.status !== 'success');
  if (script && !failed) return describeNewLayers(kernel, context.projectId, before);
  // The tiles merged before the failure stay on the canvas, billed.
  const landed = listNewLayers(kernel, context.projectId, before);
  return `Error: ${(failed?.error ?? 'the upscale did not run.').slice(0, MAX_COMMAND_ERROR_CHARS)}${landed ? ` What landed before it stays on the canvas, billed: ${landed}` : ''}`;
};

const runCall = async (kernel: RefObject<KernelHandle | null>, call: DesignerBrowserCall, context: CallContext): Promise<DesignerItem> => {
  let output: DesignerItem['output'];
  try {
    output = call.name === 'read_scene' ? await readScene(kernel, context)
      : call.name === 'view_area' ? await viewArea(kernel, call.args, context)
        : call.name === 'view_image' ? await viewImage(kernel, call.args, context)
          : call.name === 'crop_image' ? await cropImage(kernel, call.args, context)
            : DESIGNER_JNN_ACTIONS[call.name] ? await runAction(kernel, call, context)
              : Object.hasOwn(DESIGNER_SWEEP_UPSCALE_TOOLS, call.name) ? await runSweepUpscale(kernel, call, context)
              : `Error: no tool named "${call.name}".`;
  } catch (error) {
    if (context.controller.signal.aborted) throw error;
    console.error(`[designer] ${call.name} failed`, error);
    output = 'Error: the call failed.';
  }
  return { type: 'function_call_output', call_id: call.callId, output };
};

/** One output per call, in the calls' order; a call that fails answers with its error, never throws the turn (a stop does). Actions run one after the other. */
export const runDesignerBrowserCalls = async (kernel: RefObject<KernelHandle | null>, calls: DesignerBrowserCall[], context: CallContext): Promise<DesignerItem[]> => {
  // read_scene stores the tray files once per turn: two at once would store them twice.
  if (!calls.some(call => DESIGNER_JNN_ACTIONS[call.name] || DESIGNER_BILLED_TOOLS.has(call.name) || call.name === 'read_scene')) return Promise.all(calls.map(call => runCall(kernel, call, context)));
  const outputs: DesignerItem[] = [];
  for (const call of calls) outputs.push(await runCall(kernel, call, context));
  return outputs;
};
